import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Text, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Add20Regular, Board20Regular } from '@fluentui/react-icons';
import { useDashboard } from '../contexts/DashboardContext';
import CardListPage from '../components/shared/CardListPage';
import DashboardCard, { type DashboardCardItem } from '../components/dashboard-list/DashboardCard';
import SidePanel from '../components/shared/SidePanel';
import DatasetViewSelector from '../components/dashboard-creation/DatasetViewSelector';
import {
  MutationButton,
  MutationOnly,
  useWorkspaceAccess,
} from '../components/security/WorkspaceAccess';

const useStyles = makeStyles({
  createCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke2),
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transitionProperty: 'border-color, background-color, box-shadow',
    transitionDuration: '180ms',
    ':hover': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      backgroundColor: tokens.colorNeutralBackground2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    },
  },
  createIcon: {
    fontSize: '32px',
    color: tokens.colorNeutralForeground4,
  },
  createLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  createSubtitle: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    textAlign: 'center' as const,
  },
});

const SORT_OPTIONS = [
  { value: 'updated', label: 'Last Updated' },
  { value: 'created', label: 'Recently Created' },
  { value: 'name', label: 'Name A-Z' },
  { value: 'name_desc', label: 'Name Z-A' },
  { value: 'charts', label: 'Most Charts' },
];

const DashboardListPage: React.FC = () => {
  const styles = useStyles();
  const { dashboards, isLoading, duplicateDashboard, deleteDashboard, createDashboardWithView } = useDashboard();
  const { runMutation } = useWorkspaceAccess();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortValue, setSortValue] = useState('updated');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-trigger dashboard creation when navigated with ?viewId=
  useEffect(() => {
    const viewId = searchParams.get('viewId');
    const viewName = searchParams.get('viewName');
    if (viewId) {
      handleViewSelect(viewId, viewName || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Transform dashboards to card items
  const cardItems: DashboardCardItem[] = useMemo(
    () =>
      dashboards.map((d) => ({
        id: d.id,
        name: d.name,
        description: d.description,
        status: d.status,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
        chartCount: d.chartCount ?? d.charts?.length ?? 0,
        dataSourceName: d.dataSourceName,
        connectorId: d.connectorId,
        questions: d.questions,
        thumbnailUrl: d.thumbnailUrl,
      })),
    [dashboards],
  );

  // Subtitle stats for TopBar
  const subtitle = useMemo(() => {
    const total = cardItems.length;
    const aiGenerated = cardItems.filter((d) => d.status === 'ready').length;
    const totalCharts = cardItems.reduce((sum, d) => sum + d.chartCount, 0);
    return `${total} dashboards · ${aiGenerated} AI generated · ${totalCharts} charts`;
  }, [cardItems]);

  // Filtered + sorted
  const filteredItems = useMemo(() => {
    let items = cardItems;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.description ?? '').toLowerCase().includes(q),
      );
    }

    items = [...items].sort((a, b) => {
      switch (sortValue) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'charts':
          return b.chartCount - a.chartCount;
        case 'created':
          return b.createdAt.getTime() - a.createdAt.getTime();
        case 'updated':
        default:
          return b.updatedAt.getTime() - a.updatedAt.getTime();
      }
    });

    return items;
  }, [cardItems, searchQuery, sortValue]);

  const handleDuplicate = useCallback(
    (id: string) => duplicateDashboard(id),
    [duplicateDashboard],
  );

  const handleDelete = useCallback(
    async (id: string) => { await deleteDashboard(id); },
    [deleteDashboard],
  );

  const handleViewSelect = useCallback(
    (datasetViewId: string, _viewName: string) =>
      runMutation(async () => {
        try {
          setSearchParams({}, { replace: true });
          const newDashboard = await createDashboardWithView(datasetViewId);
          if (newDashboard?.id) {
            setPickerOpen(false);
            navigate(`/dashboards/${newDashboard.id}`, {
              replace: true,
              state: { generating: true, jobId: newDashboard.jobId },
            });
          }
        } catch (error) {
          console.error('Failed to create dashboard:', error);
        }
      }),
    [createDashboardWithView, navigate, runMutation, setSearchParams],
  );

  return (
    <CardListPage
      title="Dashboards"
      subtitle={subtitle}
      layout="grid"
      gridMinWidth="320px"
      searchPlaceholder="Search dashboards..."
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      sortOptions={SORT_OPTIONS}
      sortValue={sortValue}
      onSortChange={setSortValue}
      isLoading={isLoading}
      isEmpty={dashboards.length === 0}
      filteredCount={filteredItems.length}
      persistentCard={(
        <MutationOnly>
          <div
            className={styles.createCard}
            onClick={() => setPickerOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPickerOpen(true); } }}
          >
            <Add20Regular className={styles.createIcon} />
            <Text className={styles.createLabel}>Create Dashboard</Text>
            <Text className={styles.createSubtitle}>Choose a data view</Text>
          </div>
        </MutationOnly>
      )}
      emptyIcon={<Board20Regular />}
      emptyTitle="Create your first AI dashboard"
      emptyDescription="Connect a data source and let AI generate insightful dashboards for your data."
      emptyAction={(
        <MutationOnly>
          <MutationButton appearance="primary" icon={<Add20Regular />} onClick={() => setPickerOpen(true)}>
            Create Your First Dashboard
          </MutationButton>
        </MutationOnly>
      )}
    >
      {filteredItems.map((item) => (
        <DashboardCard
          key={item.id}
          item={item}
          onClick={() => navigate(`/dashboards/${item.id}`)}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
        />
      ))}
      <MutationOnly>
        <SidePanel isOpen={pickerOpen} onClose={() => setPickerOpen(false)} title="Create Dashboard" width={400}>
          <DatasetViewSelector onSelect={handleViewSelect} />
        </SidePanel>
      </MutationOnly>
    </CardListPage>
  );
};

export default DashboardListPage;